import { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { env } from 'src/constants/env';
import * as routes from 'src/constants/routes';

interface Meta {
  title: string;
  description: string;
  openGraph: {
    title: string;
    description: string;
    img: string;
    type: string;
  };
  menuIcon?: JSX.Element;
}

interface RouteModel {
  path: string;
  relativePath: string;
  title: string;
  meta?: Meta;
}

type MetaContextType = {
  useUpdateRoutesMeta: () => void;
  updateMetaInfo: (meta: Meta) => void;
};

const MetaContext = createContext<MetaContextType | undefined>(undefined);

export const MetaProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  const updateMetaInfo = useCallback((meta: Meta) => {
    document.title = meta.title;
    const metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    metaDescription.content = meta.description;

    if (meta.openGraph) {
      const title = document.querySelector('meta[property="og:title"]') as HTMLMetaElement;
      const twitterTitle = document.querySelector('meta[property="twitter:title"]') as HTMLMetaElement;
      title.content = meta.openGraph.title ?? meta.title;
      twitterTitle.content = meta.openGraph.title ?? meta.title;

      const image = document.querySelector('meta[property="og:image"]') as HTMLMetaElement;
      const twitteriImage = document.querySelector('meta[property="twitter:image"]') as HTMLMetaElement;
      image.content = window.location.origin + meta.openGraph.img;
      twitteriImage.content = window.location.origin + meta.openGraph.img;

      const description = document.querySelector('meta[property="og:description"]') as HTMLMetaElement;
      const twitterDescription = document.querySelector('meta[property="twitter:description"]') as HTMLMetaElement;
      description.content = meta.openGraph.description ?? meta.description;
      twitterDescription.content = meta.openGraph.description ?? meta.description;

      const url = document.querySelector('meta[property="og:url"]') as HTMLMetaElement;
      url.content = window.location.href;

      const canonicalUrl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      canonicalUrl.href = window.location.href;

      const type = document.querySelector('meta[property="og:type"]') as HTMLMetaElement;
      type.content = meta.openGraph.type;
    }
  }, []);

  const useUpdateRoutesMeta = useCallback(() => {
    const metaGoogleSiteVerification = document.querySelector(
      'meta[name="google-site-verification"]'
    ) as HTMLMetaElement;
    metaGoogleSiteVerification.content = env.REACT_APP_NZ_GOOGLE_SITE_VERIFICATION ?? '';

    const meta = (routes.routes as RouteModel[]).find((r: RouteModel) => r.path === location.pathname)?.meta;
    if (!meta) return;
    updateMetaInfo(meta);
  }, [location.pathname, updateMetaInfo]);

  const contextValue = useMemo(() => ({ updateMetaInfo, useUpdateRoutesMeta }), [updateMetaInfo, useUpdateRoutesMeta]);
  return <MetaContext.Provider value={contextValue}>{children}</MetaContext.Provider>;
};

export const useMetaContext = () => {
  const context = useContext(MetaContext);
  if (!context) {
    throw new Error('useMetaContext must be used within a MapContextProvider');
  }
  return context;
};
